import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { PracticeProvider, usePractice } from './hooks/usePractice'
import { ToastProvider } from './components/Toast'
import SubscriptionGate from './components/SubscriptionGate'
import ProfileCompletePrompt from './components/ProfileCompletePrompt'
import LoginPage from './pages/Login'
import SignUp from './pages/SignUp'
import Pricing from './pages/Pricing'
import PublicPricing from './pages/PublicPricing'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import JoinPractice from './pages/JoinPractice'
import Dashboard from './pages/Dashboard'
import ClientDetail from './pages/ClientDetail'
import NewRequest from './pages/NewRequest'
import RequestDetail from './pages/RequestDetail'
import ClientPortal from './pages/ClientPortal'
import Settings from './pages/Settings'
import Admin from './pages/Admin'
import SupportHome from './pages/Support/SupportHome'
import SupportArticle from './pages/Support/SupportArticle'
import SupportContact from './pages/Support/SupportContact'
import SupportSuggest from './pages/Support/SupportSuggest'
import Home from './pages/Home'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import Features from './pages/Features'
import Contact from './pages/Contact'
import FAQ from './pages/FAQ'
import ArticlesIndex from './pages/ArticlesIndex'
import Article from './pages/Article'
import PostHogTracker from './components/PostHogTracker'
import ScrollToTop from './components/ScrollToTop'
import { getSettings } from './lib/supabase'
import './index.css'

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return (
    <div className="loading-screen">
      <div className="spinner" />
      <span style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>Loading...</span>
    </div>
  )
  return user ? children : <Navigate to="/login" replace />
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>
  return user ? <Navigate to="/dashboard" replace /> : children
}

// `/` shows the marketing homepage to everyone, signed-in or not.
// Signed-in users may want to visit the homepage to share links with
// colleagues or clients. The TopNav adapts its CTAs based on auth
// state so the marketing page doesn't push "Sign in / Start trial"
// at someone who's already a customer.
function HomeRoute() {
  return <Home />
}

// Only shows profile completion prompt for practice owners on first login
function WithProfilePrompt({ children }) {
  const { user } = useAuth()
  const { practiceId, role, loading: practiceLoading } = usePractice()
  const [showPrompt, setShowPrompt] = useState(false)
  const [checked, setChecked]       = useState(false)

  useEffect(() => {
    // Only check for owners — team members don't have their own settings row
    if (!user || practiceLoading || role !== 'owner') {
      setChecked(true)
      return
    }
    getSettings(practiceId).then(({ data }) => {
      if (data && data.profile_completed === false) {
        setShowPrompt(true)
      }
      setChecked(true)
    })
  }, [user, practiceId, role, practiceLoading])

  if (!checked) return null

  return (
    <>
      {showPrompt && (
        <ProfileCompletePrompt
          userId={practiceId}
          onComplete={() => setShowPrompt(false)}
          onSkip={() => setShowPrompt(false)}
        />
      )}
      {children}
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PracticeProvider>
          <ToastProvider>
            <SubscriptionGate>
            {/* ScrollToTop resets scroll on route changes (except browser
                back/forward, where the user expects their previous
                position). Lives inside BrowserRouter to read the route. */}
            <ScrollToTop />
            {/* PostHogTracker lives inside BrowserRouter so it can read
                the current route, and outside the marketing/auth gates
                so it sees every navigation. It self-limits to marketing
                routes; see src/lib/posthog.js for the whitelist. */}
            <PostHogTracker />
            <Routes>
              {/* Public */}
              <Route path="/" element={<HomeRoute />} />
              <Route path="/login"           element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
              <Route path="/signup"          element={<SignUp />} />
              <Route path="/pricing"         element={<PublicPricing />} />
              <Route path="/pricing/account" element={<Pricing />} />
              <Route path="/terms"           element={<Terms />} />
              <Route path="/privacy"         element={<Privacy />} />
              <Route path="/features"        element={<Features />} />
              <Route path="/how-it-works"    element={<Features />} />
              <Route path="/contact"         element={<Contact />} />
              <Route path="/faq"             element={<FAQ />} />
              <Route path="/articles"        element={<ArticlesIndex />} />
              <Route path="/articles/:slug"  element={<Article />} />
              <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
              <Route path="/reset-password"  element={<ResetPassword />} />
              <Route path="/join"            element={<JoinPractice />} />
              <Route path="/request/:token"  element={<ClientPortal />} />

              {/* Protected */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <WithProfilePrompt>
                    <Dashboard />
                  </WithProfilePrompt>
                </ProtectedRoute>
              } />
              <Route path="/clients/:clientId"             element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
              <Route path="/clients/:clientId/new-request" element={<ProtectedRoute><NewRequest /></ProtectedRoute>} />
              <Route path="/requests/:requestId"           element={<ProtectedRoute><RequestDetail /></ProtectedRoute>} />
              <Route path="/settings"                      element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/admin"                         element={<ProtectedRoute><Admin /></ProtectedRoute>} />
              <Route path="/support"                       element={<ProtectedRoute><SupportHome /></ProtectedRoute>} />
              <Route path="/support/contact"               element={<ProtectedRoute><SupportContact /></ProtectedRoute>} />
              <Route path="/support/suggest"               element={<ProtectedRoute><SupportSuggest /></ProtectedRoute>} />
              <Route path="/support/:slug"                 element={<ProtectedRoute><SupportArticle /></ProtectedRoute>} />

              {/* Fallback */}
              <Route path="*" element={<HomeRoute />} />
            </Routes>
          </SubscriptionGate>
        </ToastProvider>
        </PracticeProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
